import type { AxiosResponse } from "axios";
import camelcaseKeys from "camelcase-keys";
import useSWR, { KeyedMutator } from "swr";
import { Workspace } from "@/models/workspace";
import ApiService, { defaultSWROption } from "@/services/api/api.service";
import { UpdateWorkspaceRequest, WorkspaceSchema } from "@/services/workspace/workspace.api.types";
import WorkspaceEndpoints from "@/services/workspace/workspace.endpoint";

export class WorkspaceService {
  static getWorkspaces(): {
    workspaces: Workspace[] | undefined;
    error: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<AxiosResponse<WorkspaceSchema[], undefined>>;
  } {
    const fetcher = () =>
      ApiService.fetchData<undefined, WorkspaceSchema[]>({
        url: WorkspaceEndpoints.GET_WORKSPACES,
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading, mutate } = useSWR(
      WorkspaceEndpoints.GET_WORKSPACES,
      fetcher,
      defaultSWROption,
    );

    return {
      workspaces: data?.data.map(buildWorkspace) ?? [],
      error,
      isLoading,
      mutate,
    };
  }

  static async createWorkspace(name: string, categoryMaxLevel: number) {
    await ApiService.fetchData<
      { name: string; categoryMaxLevel: number; },
      WorkspaceSchema
    >({
      url: WorkspaceEndpoints.CREATE_WORKSPACE,
      method: "POST",
      data: { name: name, categoryMaxLevel: categoryMaxLevel },
    });
  }
  static async updateWorkspace(workspaceId: string, name: string) {
    await ApiService.fetchData<
      { name: string; },
      UpdateWorkspaceRequest
    >({
      url: WorkspaceEndpoints.EDIT_WORKSPACE(workspaceId),
      method: "PUT",
      data: { name: name },
    });
  }
}

const buildWorkspace = (workspace: WorkspaceSchema): Workspace => {
  const metadata = workspace.metadata && camelcaseKeys(workspace.metadata, { deep: true });

  return new Workspace(
    workspace.id,
    workspace.name,
    workspace.categoryMaxLevel,
    new Date(workspace.createdAt),
    metadata,
  );
};
